import "dayjs/locale/id";
import { Flex, Skeleton } from "antd";
import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ScheduleSider } from "./filter/ScheduleSider";
import {
  ScheduleProvider,
  useScheduleState,
} from "./filter/ScheduleFilterContext";
import CreateScheduleDrawer, {
  CREATE_PANEL_WIDTH,
} from "./drawer/CreateScheduleDrawer";
import {
  CreateScheduleResumeDraft,
  ScheduleResumeDraft,
} from "shared/utils/scheduleResumeDraft";
import PageHeading from "components/PageHeading.component";
import BookingRefSearch from "components/bookings/BookingRefSearch.component";
import ChunkErrorBoundary from "components/ChunkErrorBoundary.component";
import { useOpenCreateFromNav } from "hooks/useOpenCreateFromNav";
import { CONTENT_PADDING_BOTTOM } from "layout/MainLayout";
import { FULL_VIEWPORT_HEIGHT } from "shared/constants/zoom";
import { translate } from "shared/languages";
import { importChunk } from "shared/utils/appVersion";
import { InstructorColorProvider } from "context/InstructorColorProvider";

// Lazy load the views for code splitting
// Through importChunk so a deploy that moved this chunk's hashed filename
// reloads the page instead of throwing during render. A failure it lets
// through lands on the ChunkErrorBoundary around the Suspense below, which
// shows a reload card instead of a blank page.
const CalendarView = lazy(() =>
  importChunk(() => import("./view/CalendarView")).then((module) => ({
    default: module.CalendarView,
  }))
);

// Horizontal shell offset the fixed create panel overlays: the collapsed icon
// nav sider (70px) + the content area's paddingLeft (30px), both set in
// MainLayout. The in-flow spacer reserves only the panel width beyond that.
const SHELL_LEFT_OFFSET = 70 + 30;

const ScheduleContent = () => {
  const { isCreateDrawerOpen, setCreateDrawerOpen } = useScheduleState();
  const location = useLocation();
  const navigate = useNavigate();
  const [resumeDraft, setResumeDraft] =
    useState<CreateScheduleResumeDraft | null>(null);

  // Dashboard hero quick action: land with the create panel already open.
  useOpenCreateFromNav(() => setCreateDrawerOpen(true));

  // Resuming after a round trip to a client's detail page (see
  // ClientPackageSection.tsx's "Kelola Paket Klien" button) — reopen the
  // panel with the draft it carries, then clear the flag from history so
  // refresh/back doesn't re-trigger it. Separate from useOpenCreateFromNav
  // above, which serves a different caller (the dashboard) and must keep
  // working unchanged. Only "create-schedule" drafts are ours to apply —
  // a "new-booking"/"add-waitlist" draft returns to a schedule DETAIL page
  // instead (see ScheduleDetails.tsx), never here.
  useEffect(() => {
    const draft = (
      location.state as { scheduleDraft?: ScheduleResumeDraft } | null
    )?.scheduleDraft;
    if (draft?.kind === "create-schedule") {
      setResumeDraft(draft);
      setCreateDrawerOpen(true);
      navigate(location.pathname + location.search, { replace: true });
    }
    // Re-runs whenever router state changes, not just on mount: resuming a
    // SAVED draft from the drafts drawer navigates to this same page, which
    // doesn't remount. Re-run-safe — consuming the draft navigates
    // {replace: true} with no state, so the next run fails the kind guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  return (
    <Flex
      gap={20}
      // Padding, not a margin: a 30px margin on top of 100% height pushed the
      // calendar's bottom edge 30px past the page, where the wrapper cut it.
      style={{
        paddingTop: 30,
        boxSizing: "border-box",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Left rail: while creating, the fixed full-height create panel overlays
          the nav sider; the spacer keeps the live calendar clear of it. The
          filter sider is restored on close. */}
      {isCreateDrawerOpen ? (
        <>
          <CreateScheduleDrawer
            resumeDraft={resumeDraft}
            onResumeDraftConsumed={() => setResumeDraft(null)}
          />
          <div
            style={{
              width: CREATE_PANEL_WIDTH - SHELL_LEFT_OFFSET,
              flexShrink: 0,
            }}
          />
        </>
      ) : (
        <ScheduleSider />
      )}
      <Flex vertical style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <PageHeading
          title={translate("schedule.name")}
          extra={<BookingRefSearch />}
        />
        <ChunkErrorBoundary>
          <Suspense fallback={<Skeleton />}>
            <CalendarView />
          </Suspense>
        </ChunkErrorBoundary>
      </Flex>
    </Flex>
  );
};

const SchedulePage = () => {
  return (
    <ScheduleProvider>
      {/* Instructor colours for the calendar AND the sider's instructor
          filter, which sits beside the calendar rather than inside it. */}
      <InstructorColorProvider>
        {/* The page fills the shell exactly: the window height (inside the
          body zoom) minus the shell's own bottom padding, or the page would
          scroll by precisely that padding. */}
        <div
          style={{
            height: `calc(${FULL_VIEWPORT_HEIGHT} - ${CONTENT_PADDING_BOTTOM}px)`,
            overflow: "hidden",
          }}
        >
          <ScheduleContent />
        </div>
      </InstructorColorProvider>
    </ScheduleProvider>
  );
};

export default SchedulePage;
